import AsyncStorage from "@react-native-async-storage/async-storage";
import {Platform} from 'react-native';
import {generateObjectId} from "./objectId";
import {getAccessToken} from "./storage";
import {ApiConfig} from "../config/ApiConfig";
import {getCurrentRouteInfo, getRouteHistory} from "./navigationRef";

const API_URL = `${ApiConfig.apiURL}/api`;

/**
 * Returns true when the configured API base URL points at a local
 * development backend. In that case we intentionally skip sending
 * error reports to the server to avoid noisy dev traffic / failures.
 */
const isLocalhostApi = () => {
  try {
    const url = String(ApiConfig.apiURL || '').toLowerCase();
    return (
      url.includes('localhost') ||
      url.includes('127.0.0.1') ||
      url.includes('10.0.2.2') || // Android emulator -> host loopback
      url.includes('0.0.0.0')
    );
  } catch (_) {
    return false;
  }
};

/**
 * Error reporting service to log errors to the server
 */
class ErrorReportingService {
  static REPORT_DEDUPE_WINDOW_MS = 15000;
  static _installedGlobalHandlers = false;
  static _globalCleanup = null;
  static _recentReports = new Map();

  static toSerializable(value, depth = 0) {
    if (depth > 4) {
      return '[MaxDepthReached]';
    }

    if (value == null) {
      return value;
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
    }

    if (Array.isArray(value)) {
      return value.map((entry) => this.toSerializable(entry, depth + 1));
    }

    if (typeof value === 'function') {
      return `[Function ${value.name || 'anonymous'}]`;
    }

    if (typeof value === 'object') {
      return Object.entries(value).reduce((accumulator, [key, nestedValue]) => {
        accumulator[key] = this.toSerializable(nestedValue, depth + 1);
        return accumulator;
      }, {});
    }

    return value;
  }

  static getErrorMessage(error) {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error?.message) {
      return String(error.message);
    }

    return 'Unknown error';
  }

  static getErrorStack(error, providedStacktrace) {
    if (typeof providedStacktrace === 'string' && providedStacktrace.length > 0) {
      return providedStacktrace;
    }

    if (Array.isArray(providedStacktrace)) {
      return providedStacktrace.join('\n');
    }

    if (error instanceof Error) {
      return error.stack || '';
    }

    return error?.stack || '';
  }

  static getErrorCode(error, fullJSON) {
    return error?.code || error?.status || error?.statusCode ||
      (error?.response && error.response.status) ||
      (fullJSON && fullJSON.code) || 'unknown';
  }

  /**
   * Extract the raw JavaScript stack (the WHERE-in-code) directly off the error
   * object, kept separate from any React component stack so the two are never
   * conflated in the report.
   * @param {Error|object|string} error
   * @returns {string|null}
   */
  static getRawErrorStack(error) {
    if (error instanceof Error && error.stack) {
      return error.stack;
    }
    if (error && typeof error === 'object' && typeof error.stack === 'string') {
      return error.stack;
    }
    return null;
  }

  /**
   * Pull the React component stack (the WHICH-components-rendered chain) out of
   * the additional info. The error boundary forwards it as `boundaryStack`;
   * other callers may pass `componentStack` explicitly.
   * @param {object} additionalInfo - Already-serialized additional info.
   * @returns {string|null}
   */
  static getComponentStack(additionalInfo) {
    const stack = additionalInfo?.componentStack || additionalInfo?.boundaryStack;
    if (typeof stack === 'string' && stack.length > 0) {
      return stack;
    }
    if (Array.isArray(stack)) {
      return stack.join('\n');
    }
    return null;
  }

  /**
   * Capture which screen the user was on and how they got there. This survives
   * the NavigationContainer unmounting into the error fallback (see
   * `navigationRef.js`), so even a fatal render crash reports its origin screen.
   * @returns {{currentRoute: (string|null), currentRouteParams: any, routeHistory: Array}}
   */
  static getNavigationContext() {
    try {
      const current = getCurrentRouteInfo();
      return {
        currentRoute: current?.name || null,
        currentRouteParams: this.toSerializable(current?.params ?? null),
        routeHistory: getRouteHistory(),
      };
    } catch (_) {
      return {currentRoute: null, currentRouteParams: null, routeHistory: []};
    }
  }

  /**
   * Capture device/runtime context so backend reports can tell native Android
   * crashes apart by OS level / engine (the backend only enriches browser/IP
   * fields for the web build, never for the native app).
   * @returns {{platform: string, platformVersion: (string|null), engine: string, appVersion: (string|null), isDev: boolean}}
   */
  static getDeviceContext() {
    let platformVersion = null;
    try {
      platformVersion = Platform.Version != null ? String(Platform.Version) : null;
    } catch (_) {
      platformVersion = null;
    }

    let engine = 'unknown';
    try {
      engine = (typeof globalThis !== 'undefined' && globalThis.HermesInternal) ? 'hermes' : 'jsc';
    } catch (_) {
      engine = 'unknown';
    }

    let appVersion = null;
    try {
      // expo-constants is a core Expo dependency; guard anyway so the reporter
      // never throws if it is unavailable on a given platform.
      const Constants = require('expo-constants').default;
      appVersion = Constants?.expoConfig?.version || Constants?.nativeAppVersion || null;
    } catch (_) {
      appVersion = null;
    }

    return {
      platform: Platform.OS,
      platformVersion,
      engine,
      appVersion,
      isDev: Boolean(typeof __DEV__ !== 'undefined' && __DEV__),
    };
  }

  static shouldSkipDuplicate(signature) {
    const now = Date.now();

    for (const [existingSignature, timestamp] of this._recentReports.entries()) {
      if (now - timestamp > this.REPORT_DEDUPE_WINDOW_MS) {
        this._recentReports.delete(existingSignature);
      }
    }

    const previousTimestamp = this._recentReports.get(signature);
    if (previousTimestamp && now - previousTimestamp < this.REPORT_DEDUPE_WINDOW_MS) {
      return true;
    }

    this._recentReports.set(signature, now);
    return false;
  }

  /**
   * Report an error to the server
   * @param {Error|string} error - The error object or error message
   * @param {Object} additionalInfo - Additional information about the error context
   * @returns {Promise<void>}
   */
  static async reportError(error, additionalInfo = {}) {
    try {
      const errorId = generateObjectId();
      const restaurantId = await this.getRestaurantId();
      const serializedAdditionalInfo = this.toSerializable(additionalInfo);

      // Extract error message
      const errorMessage = this.getErrorMessage(error);

      // Separate the two kinds of stack so reports are actually diagnosable:
      //  - errorStack: raw JS stack — WHERE in the code it threw.
      //  - componentStack: React render chain — WHICH components were mounting.
      // The legacy `stacktrace` field is kept (backend depends on it) and prefers
      // the JS stack, falling back to the component stack so it is never empty.
      const errorStack = this.getRawErrorStack(error);
      const componentStack = this.getComponentStack(serializedAdditionalInfo);
      const stackTrace = this.getErrorStack(
        error,
        errorStack || serializedAdditionalInfo?.stacktrace || componentStack
      );

      // Capture where the user was and the device they were on. Done for EVERY
      // report path (boundary, fallback, global handler, unhandled rejection).
      const navigationContext = this.getNavigationContext();
      const deviceContext = this.getDeviceContext();

      // Try to parse the error as JSON if it's a string
      let fullJSON = null;
      try {
        const NativeResponse = globalThis?.Response;
        if (typeof error === 'string') {
          fullJSON = JSON.parse(error);
        } else if (typeof NativeResponse !== 'undefined' && error instanceof NativeResponse) {
          fullJSON = await error.clone().json();
        } else if (error instanceof Error && error.response) {
          fullJSON = error.response;
        } else if (typeof error === 'object') {
          fullJSON = this.toSerializable(error);
        }
      } catch (_e) {
        // If parsing fails, just use the error as is
        fullJSON = error instanceof Error ? { message: error.message } : error;
      }

      // Extract error code if available
      const errorCode = this.getErrorCode(error, fullJSON);

      const duplicateSignature = [
        serializedAdditionalInfo?.source || serializedAdditionalInfo?.context || 'unknown-source',
        serializedAdditionalInfo?.phase || 'default-phase',
        navigationContext.currentRoute || 'no-route',
        errorCode,
        errorMessage,
      ].join(':');

      if (!serializedAdditionalInfo?.forceReport && this.shouldSkipDuplicate(duplicateSignature)) {
        return null;
      }

      // Prepare the error report payload. Send `null` (not the string 'unknown')
      // when the restaurant isn't known yet — the backend then falls back to the
      // authenticated user's restaurant and avoids an ObjectId cast failure that
      // would drop the report (and its e-mail) entirely.
      const errorReport = {
        restaurantId: restaurantId || null,
        error: errorMessage,
        errorId: errorId,
        errorInfo: {
          errorCode: errorCode,
          errorString: errorMessage,
          stacktrace: stackTrace,
          fullJSON: fullJSON,
          message: errorMessage,
          ...serializedAdditionalInfo,
          // Authoritative diagnostics — kept AFTER the spread so a caller can
          // never accidentally overwrite them. These are the fields that turn an
          // "unknown, can't fix" report into an actionable one.
          errorStack: errorStack,
          componentStack: componentStack,
          currentRoute: navigationContext.currentRoute,
          currentRouteParams: navigationContext.currentRouteParams,
          routeHistory: navigationContext.routeHistory,
          device: deviceContext,
        },
        extraInfo: {
          orderCode: serializedAdditionalInfo.orderCode || serializedAdditionalInfo.order?.orderCode,
          generatedQRCode: serializedAdditionalInfo.generatedQRCode,
          order: serializedAdditionalInfo.order,
          stackTrace,
          timestamp: new Date().toISOString(),
          platform: Platform.OS,
          ...serializedAdditionalInfo,
          errorStack: errorStack,
          componentStack: componentStack,
          currentRoute: navigationContext.currentRoute,
          currentRouteParams: navigationContext.currentRouteParams,
          routeHistory: navigationContext.routeHistory,
          device: deviceContext,
        }
      };

      // Send the error report to the server
      await this.sendErrorReport(errorReport);

      console.log(`Error reported with ID: ${errorId}`);
      return errorId;
    } catch (reportingError) {
      // Don't throw from the error reporter to avoid infinite loops
      console.error('Failed to report error:', reportingError);
    }
  }

  static async reportBoundaryError(error, stacktrace, additionalInfo = {}) {
    return this.reportError(error, {
      source: 'ErrorBoundary',
      phase: 'caught',
      boundaryStack: stacktrace,
      ...additionalInfo,
    });
  }

  static async reportErrorScreenShown(error, additionalInfo = {}) {
    return this.reportError(error, {
      source: 'ErrorFallbackScreen',
      phase: 'displayed',
      ...additionalInfo,
    });
  }

  static installGlobalHandlers(additionalInfo = {}) {
    if (this._installedGlobalHandlers) {
      return this._globalCleanup || (() => {});
    }

    this._installedGlobalHandlers = true;
    const cleanups = [];

    if (globalThis.ErrorUtils?.getGlobalHandler && globalThis.ErrorUtils?.setGlobalHandler) {
      const previousHandler = globalThis.ErrorUtils.getGlobalHandler();

      globalThis.ErrorUtils.setGlobalHandler((error, isFatal) => {
        this.reportError(error, {
          source: 'GlobalErrorUtils',
          phase: 'uncaught',
          isFatal: Boolean(isFatal),
          ...additionalInfo,
        });

        if (typeof previousHandler === 'function') {
          previousHandler(error, isFatal);
        }
      });

      cleanups.push(() => {
        try {
          globalThis.ErrorUtils.setGlobalHandler(previousHandler);
        } catch (error) {
          console.warn('Failed to restore previous global error handler:', error?.message || error);
        }
      });
    }

    if (typeof globalThis.addEventListener === 'function') {
      const handleUnhandledRejection = (event) => {
        const reason = event?.reason || event;
        this.reportError(reason instanceof Error ? reason : new Error(this.getErrorMessage(reason)), {
          source: 'UnhandledPromiseRejection',
          phase: 'global',
          ...additionalInfo,
        });
      };

      const handleGlobalError = (event) => {
        const eventError = event?.error || new Error(event?.message || 'Global runtime error');
        this.reportError(eventError, {
          source: 'GlobalWindowError',
          phase: 'global',
          filename: event?.filename,
          lineNumber: event?.lineno,
          columnNumber: event?.colno,
          ...additionalInfo,
        });
      };

      globalThis.addEventListener('unhandledrejection', handleUnhandledRejection);
      globalThis.addEventListener('error', handleGlobalError);

      cleanups.push(() => {
        globalThis.removeEventListener?.('unhandledrejection', handleUnhandledRejection);
        globalThis.removeEventListener?.('error', handleGlobalError);
      });
    }

    this._globalCleanup = () => {
      cleanups.forEach((cleanup) => {
        try {
          cleanup();
        } catch (error) {
          console.warn('Global error handler cleanup failed:', error?.message || error);
        }
      });
      this._installedGlobalHandlers = false;
      this._globalCleanup = null;
    };

    return this._globalCleanup;
  }

  /**
   * Get the restaurant ID from AsyncStorage
   * @returns {Promise<string|null>}
   */
  static async getRestaurantId() {
    try {
      const restaurantData = await AsyncStorage.getItem('restaurant');
      if (restaurantData) {
        const restaurant = JSON.parse(restaurantData);
        return restaurant._id;
      }
      return null;
    } catch (error) {
      console.error('Error fetching restaurant ID:', error);
      return null;
    }
  }

  /**
   * Send the error report to the server
   * @param {Object} errorReport - The error report payload
   * @returns {Promise<void>}
   */
  static async sendErrorReport(errorReport) {
    // Skip reporting to backend when pointing at a local dev API.
    if (isLocalhostApi()) {
      if (__DEV__) {
        console.log('[ErrorReportingService] Skipping error report on localhost:', errorReport);
      }
      return {skipped: true, reason: 'localhost'};
    }

    // POST the crash/error to the backend error-logging endpoint. The backend
    // (POST /api/error/log) records the occurrence and e-mails the service desk
    // (support@happygastro.hu) the first time a new error signature is seen, so
    // enabling this call is what turns "on crash, send us an e-mail" on.
    const accessToken = await getAccessToken();

    const response = await fetch(`${API_URL}/error/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': accessToken ? `Bearer ${accessToken}` : '',
      },
      body: JSON.stringify(errorReport),
    });

    if (!response.ok) {
      throw new Error(`Error reporting failed with status: ${response.status}`);
    }

    // Some error responses may legitimately carry no JSON body; never let a
    // parse failure bubble up into reportError()'s catch (which would only log).
    return response.json().catch(() => ({}));
  }

  /**
   * Log information to console and prepare for future API storage
   * @param {string} level - Log level (info, warn, error)
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log (component, function, etc.)
   * @returns {void}
   */
  static log(level = 'info', message, data = {}, source = '') {
    // Format the log message
    const timestamp = new Date().toISOString();
    const formattedMessage = `[${timestamp}] [${level.toUpperCase()}] ${source ? `[${source}]` : ''} ${message}`;

    // Log to console based on level
    switch (level.toLowerCase()) {
      case 'error':
        console.error(formattedMessage, data);
        break;
      case 'warn':
        console.warn(formattedMessage, data);
        break;
      case 'info':
      default:
        console.log(formattedMessage, data);
        break;
    }

    // TODO: In the future, this will also send logs to the API
    // For now, we're just logging to the console
  }

  /**
   * Convenience method for info logs
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log
   */
  static info(message, data = {}, source = '') {
    this.log('info', message, data, source);
  }

  /**
   * Convenience method for warning logs
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log
   */
  static warn(message, data = {}, source = '') {
    this.log('warn', message, data, source);
  }

  /**
   * Convenience method for error logs
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log
   */
  static error(message, data = {}, source = '') {
    this.log('error', message, data, source);
  }
}

export default ErrorReportingService;
